/** Supplied at document start by the trusted native CRM container. */
export interface NativeCallsCapability {
  version: 1;
  mediaOwner: 'native';
  sessionOwner: 'native';
  deviceId: string;
}

export function nativeCallsCapability(): NativeCallsCapability | null {
  if (typeof window === 'undefined') return null;
  const value = (window as Window & { JemaCallsNative?: unknown }).JemaCallsNative;
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<NativeCallsCapability>;
  if (
    candidate.version !== 1 ||
    candidate.mediaOwner !== 'native' ||
    candidate.sessionOwner !== 'native' ||
    typeof candidate.deviceId !== 'string' ||
    !candidate.deviceId.trim()
  ) {
    return null;
  }
  return candidate as NativeCallsCapability;
}

/** Supabase can refresh from getSession even with its refresh timer disabled. */
export function nativeOwnedAuthFetch(supabaseUrl: string): typeof fetch {
  const tokenUrl = new URL('/auth/v1/token', supabaseUrl);
  return async (input, init) => {
    const url = new URL(
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    );
    if (
      !nativeCallsCapability() ||
      url.origin !== tokenUrl.origin ||
      url.pathname !== tokenUrl.pathname ||
      url.searchParams.get('grant_type') !== 'refresh_token'
    )
      return fetch(input, init);
    const host = window as Window & {
      flutter_inappwebview?: { callHandler(name: string, payload: unknown): Promise<unknown> };
    };
    if (!host.flutter_inappwebview) throw new Error('Native session owner unavailable');
    const value = (await host.flutter_inappwebview.callHandler('jemaCallsV2', {
      version: 1,
      type: 'refreshSession',
    })) as Record<string, unknown> | null;
    if (
      !value ||
      typeof value.access_token !== 'string' ||
      !value.access_token ||
      typeof value.refresh_token !== 'string' ||
      !value.refresh_token ||
      typeof value.expires_at !== 'number' ||
      value.expires_at <= Date.now() / 1000 ||
      !value.user ||
      typeof value.user !== 'object' ||
      typeof (value.user as { id?: unknown }).id !== 'string'
    )
      throw new Error('Native session refresh unavailable');
    return new Response(
      JSON.stringify({
        ...value,
        token_type: 'bearer',
        expires_in: Math.max(1, Math.floor(value.expires_at - Date.now() / 1000)),
      }),
      { status: 200, headers: { 'Content-Type': 'application/json' } }
    );
  };
}

/** CRM call links transfer intent to the native owner before browser media guards. */
export async function invokeNativeCallAction(payload: Record<string, unknown>): Promise<boolean> {
  const host = window as Window & {
    flutter_inappwebview?: { callHandler(name: string, payload: unknown): Promise<unknown> };
  };
  if (!nativeCallsCapability() || !host.flutter_inappwebview) return false;
  try {
    return (
      (await host.flutter_inappwebview.callHandler('jemaCallsV2', {
        ...payload,
        version: 1,
        type: 'action',
      })) === true
    );
  } catch {
    return false;
  }
}
